import { useState, useMemo, useEffect, useRef } from 'react';
import { Heart } from 'lucide-react';
import { useFavorites } from '@/context/FavoritesContext';
import { useScrollPosition } from '@/hooks/useScrollPosition';
import { FavoritesPanelHeader } from './FavoritesPanelHeader';
import { FavoritesSearch } from './FavoritesSearch';
import { FavoritesFilters } from './FavoritesFilters';
import { FavoritesSortDropdown } from './FavoritesSortDropdown';
import { FavoritesListManager } from './FavoritesListManager';
import { FavoriteItemCard } from './FavoriteItemCard';
import type { FavoriteFilterType, FavoriteSortOption } from '@/types/favorites';

interface FavoritesPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export function FavoritesPanel({ isOpen, onClose }: FavoritesPanelProps) {
  const {
    favorites,
    totalCount,
    loading,
    getFilteredFavorites,
    getSortedFavorites,
    searchFavorites,
  } = useFavorites();

  // Local state for filters, sort, and search
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<FavoriteFilterType>('all');
  const [sortOption, setSortOption] = useState<FavoriteSortOption>('date_added_desc');
  const [selectedListId, setSelectedListId] = useState<string | null>(null);

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const scrollRef = useRef<HTMLDivElement>(null);

  // Preserve scroll position when panel is reopened
  useScrollPosition({
    key: 'favorites-panel',
    scrollRef,
    enabled: isOpen,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Calculate counts for filter tabs
  const counts = useMemo(
    () => ({
      all: favorites.length,
      resource: favorites.filter((f) => f.itemType === 'resource').length,
      message: favorites.filter((f) => f.itemType === 'message').length,
      file: favorites.filter((f) => f.itemType === 'file').length,
      prayer: favorites.filter((f) => f.itemType === 'prayer').length,
      verse: favorites.filter((f) => f.itemType === 'verse').length,
    }),
    [favorites]
  );

  // Get filtered, sorted, and searched favorites
  const displayedFavorites = useMemo(() => {
    // First filter by type and list
    let filtered = getFilteredFavorites(activeFilter, selectedListId || undefined);

    // Then search
    if (debouncedSearch) {
      filtered = searchFavorites(debouncedSearch, filtered);
    }

    // Then sort
    return getSortedFavorites(filtered, sortOption);
  }, [
    activeFilter,
    selectedListId,
    debouncedSearch,
    sortOption,
    getFilteredFavorites,
    getSortedFavorites,
    searchFavorites,
  ]);

  const emptyMessageWhenNoFilter =
    activeFilter !== 'all' ? (
      <>
        <h3 className="text-lg font-medium text-slate-600 mb-1">No {activeFilter}s saved yet</h3>
        <p className="text-sm text-slate-400 max-w-xs">
          Save {activeFilter}s by clicking the heart icon on items you want to keep.
        </p>
      </>
    ) : (
      <>
        <h3 className="text-lg font-medium text-slate-600 mb-1">No favorites yet</h3>
        <p className="text-sm text-slate-400 max-w-xs">
          Save items to find them here! Click the heart icon on resources, posts, materials, or
          prayers to add them to your favorites.
        </p>
      </>
    );
  const emptyMessageWhenNoList = selectedListId ? (
    <>
      <h3 className="text-lg font-medium text-slate-600 mb-1">This list is empty</h3>
      <p className="text-sm text-slate-400 max-w-xs">
        Add favorites to this list by clicking the menu on any favorite item.
      </p>
    </>
  ) : (
    emptyMessageWhenNoFilter
  );
  const emptyStateMessage = debouncedSearch ? (
    <>
      <h3 className="text-lg font-medium text-slate-600 mb-1">No matches found</h3>
      <p className="text-sm text-slate-400 max-w-xs">
        No favorites match "{debouncedSearch}". Try a different search term.
      </p>
    </>
  ) : (
    emptyMessageWhenNoList
  );
  const emptyStateContent = (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="bg-slate-100 p-4 rounded-full mb-4">
        <Heart size={32} className="text-slate-300" />
      </div>
      {emptyStateMessage}
    </div>
  );
  const contentWhenLoaded =
    displayedFavorites.length === 0 ? (
      emptyStateContent
    ) : (
      <div className="space-y-3">
        {displayedFavorites.map((item) => (
          <FavoriteItemCard key={item.id} item={item} onClosePanel={onClose} />
        ))}
      </div>
    );

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Prevent body scroll when panel is open - use CSS class for cleaner DOM manipulation
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }

    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 z-40 animate-fadeIn" onClick={onClose} />

      {/* Panel */}
      <div
        className="fixed inset-x-0 bottom-0 z-50 flex flex-col bg-slate-50 animate-slideIn"
        style={{
          top: 'calc(var(--header-height, 56px) + var(--safe-area-top, 0px))',
          maxHeight:
            'calc(var(--app-height, 100vh) - var(--header-height, 56px) - var(--safe-area-top, 0px))',
        }}
      >
        {/* Header */}
        <FavoritesPanelHeader totalCount={totalCount} onClose={onClose} />

        {/* Search */}
        <FavoritesSearch value={searchQuery} onChange={setSearchQuery} />

        {/* Filters */}
        <FavoritesFilters
          activeFilter={activeFilter}
          onFilterChange={setActiveFilter}
          counts={counts}
        />

        {/* Sort */}
        <FavoritesSortDropdown value={sortOption} onChange={setSortOption} />

        {/* Lists Manager */}
        <FavoritesListManager selectedListId={selectedListId} onSelectList={setSelectedListId} />

        {/* Content */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              <div className="animate-pulse">Loading favorites...</div>
            </div>
          ) : (
            contentWhenLoaded
          )}
        </div>
      </div>
    </>
  );
}
